import React, { useCallback, useEffect, useState } from 'react';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  strapiFetchKupPdf,
  strapiFetchQuotationPdf,
  strapiOpenQuotationPdfHtmlPreview,
  strapiRegenerateKupPdf,
  strapiRegenerateQuotationPdf,
  type QuotationPdfKind,
} from '../../api/strapiCrm';

type Props = {
  open: boolean;
  pdfKind: QuotationPdfKind;
  documentId: string | null;
  quotationNumber?: string | null;
  /** Nama perusahaan (customer) untuk nama file unduh. */
  companyName?: string | null;
  jwt: string | null;
  showCustomPdfButton?: boolean;
  onClose: () => void;
  onRegenerateSuccess?: (message: string) => void;
  onRegenerateError?: (message: string) => void;
};

function pdfStatusLabel(kind: QuotationPdfKind, status: string): string {
  const label = kind === 'kup' ? 'PDF KUP' : 'PDF quotation';
  if (status === 'queued') return `${label} dalam antrean…`;
  if (status === 'processing') return `${label} sedang dibuat…`;
  return `Menunggu ${label} (dibuat saat SPV setujui)…`;
}

function pdfDialogTitle(kind: QuotationPdfKind, number: string): string {
  if (kind === 'kup') return `PDF KUP — ${number}`;
  return `PDF Quotation — ${number}`;
}

/** Buang karakter yang tidak aman untuk nama file. */
function sanitizeFilePart(value: string): string {
  return value
    .trim()
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function pdfFileName(
  kind: QuotationPdfKind,
  opts: {
    companyName?: string | null;
    quotationNumber?: string | null;
    documentId?: string | null;
  },
): string {
  const company = sanitizeFilePart(opts.companyName ?? '');
  const number = sanitizeFilePart(opts.quotationNumber ?? '') || sanitizeFilePart(opts.documentId ?? '') || 'document';
  const base = company ? `${company}_${number}` : number;
  return kind === 'kup' ? `${base}_KUP.pdf` : `${base}.pdf`;
}

export function QuotationPdfPreviewDialog({
  open,
  pdfKind,
  documentId,
  quotationNumber,
  companyName,
  jwt,
  showCustomPdfButton = false,
  onClose,
  onRegenerateSuccess,
  onRegenerateError,
}: Props) {
  const [statusText, setStatusText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [resolvedNumber, setResolvedNumber] = useState<string | null>(quotationNumber ?? null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const loadPdf = useCallback(async () => {
    if (!documentId || !jwt) return;

    setLoading(true);
    setError(null);
    setStatusText(pdfStatusLabel(pdfKind, ''));
    setResolvedNumber(quotationNumber ?? null);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    const fetchPdf = pdfKind === 'kup' ? strapiFetchKupPdf : strapiFetchQuotationPdf;

    try {
      const { blob, quotationNumber: qn } = await fetchPdf(jwt, documentId, {
        onStatus: (status) => setStatusText(pdfStatusLabel(pdfKind, status)),
      });
      if (qn?.trim()) setResolvedNumber(qn.trim());
      setPdfUrl(URL.createObjectURL(blob));
      setStatusText(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Gagal memuat ${pdfKind === 'kup' ? 'PDF KUP' : 'PDF quotation'}.`);
    } finally {
      setLoading(false);
    }
  }, [documentId, jwt, pdfKind, quotationNumber]);

  useEffect(() => {
    if (!open || !documentId || !jwt) return undefined;
    void loadPdf();
    return undefined;
  }, [open, documentId, jwt, pdfKind, reloadToken, loadPdf]);

  useEffect(() => {
    if (open) return undefined;
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setError(null);
    setStatusText(null);
    setLoading(false);
    setRegenerating(false);
    return undefined;
  }, [open]);

  function handleClose() {
    onClose();
  }

  function handleDownload() {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = pdfFileName(pdfKind, {
      companyName,
      quotationNumber: resolvedNumber,
      documentId,
    });
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function handleRegenerate() {
    if (!documentId || !jwt || regenerating) return;
    setRegenerating(true);
    setError(null);

    const regenerate =
      pdfKind === 'kup' ? strapiRegenerateKupPdf : strapiRegenerateQuotationPdf;
    const label = pdfKind === 'kup' ? 'PDF KUP' : 'PDF quotation';
    const number = resolvedNumber?.trim() || quotationNumber?.trim() || documentId;

    try {
      await regenerate(jwt, documentId, {
        onStatus: (status) => setStatusText(pdfStatusLabel(pdfKind, status)),
      });
      onRegenerateSuccess?.(`${label} ${number} berhasil di-generate ulang.`);
      setReloadToken((t) => t + 1);
    } catch (e) {
      const message = e instanceof Error ? e.message : `Gagal generate ulang ${label}.`;
      setError(message);
      onRegenerateError?.(message);
    } finally {
      setRegenerating(false);
    }
  }

  async function handleCustomPdf() {
    if (!documentId || !jwt || pdfKind !== 'quotation') return;
    try {
      await strapiOpenQuotationPdfHtmlPreview(jwt, documentId);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Gagal membuka Custom PDF preview.';
      setError(message);
      onRegenerateError?.(message);
    }
  }

  const titleNumber = resolvedNumber?.trim() || quotationNumber?.trim() || documentId || 'Quotation';
  const busy = loading || regenerating;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="quotation-pdf-preview-title"
      data-testid={`quotation-pdf-preview-dialog-${pdfKind}`}
    >
      <DialogTitle id="quotation-pdf-preview-title" sx={{ pr: 6 }}>
        {pdfDialogTitle(pdfKind, titleNumber)}
        <IconButton
          aria-label="Tutup"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, height: { xs: '70vh', md: '80vh' }, bgcolor: 'grey.100' }}>
        {error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}

        {!error && busy && !pdfUrl ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', gap: 2, p: 3 }}>
            <CircularProgress />
            {statusText ? (
              <Typography variant="body2" color="text.secondary">
                {statusText}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {!error && pdfUrl ? (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {regenerating ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  bgcolor: 'rgba(255,255,255,0.72)',
                  gap: 1,
                }}
              >
                <CircularProgress size={28} />
                {statusText ? (
                  <Typography variant="body2" color="text.secondary">
                    {statusText}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
            <Box
              component="iframe"
              src={pdfUrl}
              title={pdfDialogTitle(pdfKind, titleNumber)}
              sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
            />
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Button
          onClick={() => void handleRegenerate()}
          disabled={!documentId || !jwt || busy}
          startIcon={regenerating ? <CircularProgress size={16} color="inherit" /> : <AutorenewOutlinedIcon />}
        >
          Generate ulang
        </Button>
        {showCustomPdfButton && pdfKind === 'quotation' ? (
          <Button
            onClick={() => void handleCustomPdf()}
            disabled={!documentId || !jwt || busy}
            startIcon={<OpenInNewOutlinedIcon />}
          >
            Custom PDF
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleDownload} disabled={!pdfUrl || busy} startIcon={<DownloadOutlinedIcon />}>
          Unduh PDF
        </Button>
        <Button onClick={handleClose}>Tutup</Button>
      </DialogActions>
    </Dialog>
  );
}
